import React, { useState, ChangeEvent, useEffect, useContext } from "react";
import {
  Button,
  Close,
  Dropdown,
  HelpDrawer,
  HelpDrawerToggle,
} from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../helpers/intlHooks";
import { getDrugsByLetter } from "../../api";
import { AutocompleteDrug } from "../../@types";
import { FormattedMessage } from "react-intl-phraseapp";
import { StickyFooter } from "../../components";
import { sendTealiumEvent } from "../../helpers";
import { AppContext } from "../../app/store";

export interface AtoZDrugListProps {
  onAddDrug: (drug: AutocompleteDrug) => void;
  onOpen?: () => void;
}

const AtoZDrugList: React.FunctionComponent<AtoZDrugListProps> = ({
  onAddDrug,
  onOpen = (): void => {
    null;
  },
}) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [drugList, setDrugList] = useState([] as AutocompleteDrug[]);
  const [drugLetter, setDrugLetter] = useState("");
  const [selectedDrug, setSelectedDrug] = useState({} as AutocompleteDrug);

  const observeScroll = (event: Event): void => {
    const { target } = event;
    const selectContainer = document.querySelector(
      ".AtoZDrugList__selectContainer"
    );

    if (target && selectContainer) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (target.scrollTop > 10) {
        selectContainer.classList.add(
          "AtoZDrugList__selectContainer--shadowed"
        );
      } else {
        selectContainer.classList.remove(
          "AtoZDrugList__selectContainer--shadowed"
        );
      }
    }
  };

  const lookupDrugsByLetter = async (): Promise<void> => {
    if (drugLetter) {
      try {
        setDrugList(await getDrugsByLetter(drugLetter, state.year));
      } catch (_e) {
        // do nothing, the list will be empty
      }
    }
  };

  const ucFirst = (drugName: string): string => {
    return drugName.substring(0, 1).toUpperCase() + drugName.substring(1);
  };

  const makeAlphabet = (): string[] => {
    let curr = "A".charCodeAt(0);
    const final = "Z".charCodeAt(0);
    const alphabet = [];
    for (; curr <= final; curr++) {
      alphabet.push(String.fromCharCode(curr));
    }
    return alphabet;
  };

  useEffect(() => {
    if (open) {
      const drawer = document.querySelector(
        ".AtoZDrugList .ds-c-help-drawer__body"
      );

      if (drawer) {
        drawer.addEventListener("scroll", observeScroll);
      }

      setDrugLetter("A");
    }
  }, [open]);

  useEffect(() => {
    lookupDrugsByLetter();
  }, [drugLetter, state.year]);

  const resetSelections = (): void => {
    setDrugList([]);
    setSelectedDrug({} as AutocompleteDrug);
  };

  return (
    <div className="AtoZDrugList">
      <HelpDrawerToggle
        helpDrawerOpen={open}
        showDrawer={(): void => {
          onOpen();
          setOpen(true);
          sendTealiumEvent("Find Plans - Add Drugs", "A-Z List");
        }}
      >
        {t("drug_search.browse_drugs_az")}
      </HelpDrawerToggle>
      {open && (
        <div className="mct-c-help-drawer">
          <HelpDrawer
            className="AtoZDrugList__drawer"
            onCloseClick={(): void => setOpen(false)}
            heading={t("drug_search.browse_drugs_az")}
            closeButtonText={<Close />}
          >
            <div className="AtoZDrugList__drawerBody">
              <div className="AtoZDrugList__selectContainer">
                <Dropdown
                  label={t("drug_search.select_a_letter")}
                  labelClassName="AtoZDrugList__selectLabel"
                  size="small"
                  name="A to Z Drug List Select"
                  className="AtoZDrugList__select"
                  value={drugLetter}
                  id="browse-select-letter"
                  onChange={(event: ChangeEvent<HTMLSelectElement>): void => {
                    resetSelections();
                    setDrugLetter(event.target.value);
                  }}
                  options={makeAlphabet().map(letter => ({
                    label: letter,
                    value: letter,
                  }))}
                />
              </div>
              <div className="AtoZDrugList__resultsContainer">
                <div className="AtoZDrugList__total">
                  <b>{drugLetter}</b> (
                  <FormattedMessage
                    id="drug_search.drugs_found"
                    values={{ count: drugList.length }}
                  />
                  )
                </div>
                <ul className="AtoZDrugList__results">
                  {drugList.map((drug, index) => (
                    <li key={index}>
                      <input
                        id={drug.rxcui}
                        type="radio"
                        name="AtoZDrug"
                        onChange={(): void => {
                          setSelectedDrug(drug);
                        }}
                      />
                      <label
                        htmlFor={drug.rxcui}
                        className={
                          drug.name === selectedDrug.name ? "selected" : ""
                        }
                      >
                        {ucFirst(drug.name)}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              {selectedDrug.name && (
                <StickyFooter
                  className="AtoZDrugList__confirm"
                  id="drug-list-footer"
                >
                  <p>{ucFirst(selectedDrug.name)}</p>
                  <div className="AtoZDrugList__confirmActions">
                    <Button
                      id="aToZAddDrug"
                      aria-label={`${t("drug_search.add_drug")} ${
                        selectedDrug.name
                      }`}
                      onClick={(): void => {
                        resetSelections();
                        setOpen(false);
                        onAddDrug(selectedDrug);
                        sendTealiumEvent("Browse Drugs", "Add Drugs");
                      }}
                    >
                      {t("drug_search.add_drug")}
                    </Button>
                    <Button
                      className="ds-c-button--transparent ds-c-button--inverse"
                      onClick={(): void =>
                        setSelectedDrug({} as AutocompleteDrug)
                      }
                    >
                      {t("cancel")}
                    </Button>
                  </div>
                </StickyFooter>
              )}
            </div>
          </HelpDrawer>
        </div>
      )}
    </div>
  );
};

export default AtoZDrugList;
